import { Button, Col, Row, Upload } from "antd";
import React, { useState } from "react";
import {
  AUTH_TOKEN,
  DOMAIN_ID,
  ORG_ID,
  REACT_APP_API_TENANTS_URL,
} from "../../../constants";
import axios from "axios";
import { InboxOutlined } from "@ant-design/icons";

export default function TenantLogo({
  notification,
  setTenantLogo,
  tenantLogo,
}) {
  const [isChangingLogo, setIsChangingLogo] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const validateFileName = (file) => {
    // Check if filename contains spaces or special characters
    const fileName = file.name;
    const invalidChars = /[^a-zA-Z0-9._-]/g;

    if (invalidChars.test(fileName)) {
      notification.open({
        type: "error",
        content:
          "Filename contains invalid characters. Use only letters, numbers, periods, underscores, and hyphens.",
      });
      return false;
    }
    return true;
  };

  const validateImageDimensions = (file) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(img.src);
        const width = img.width;
        const height = img.height;
        const aspectRatio = width / height;

        // Check if aspect ratio is approximately 16:9 (allowing small deviation)
        const isValidRatio = Math.abs(aspectRatio - 16 / 9) < 0.1;

        if (!isValidRatio) {
          notification.open({
            type: "error",
            content:
              "Image should have a 16:9 aspect ratio (preferred dimensions: 1920x1080 pixels).",
          });
          resolve(false);
        } else {
          resolve(true);
        }
      };
    });
  };
  const handleLogoUpload = async (file) => {
    if (!file) {
      notification.open({
        type: "error",
        content: "No file selected",
      });
      return false;
    }

    setLogoUploading(true);
    const formData = new FormData();
    formData.append("orgId", ORG_ID());
    formData.append("tenantId", DOMAIN_ID());
    formData.append("logo", file);

    try {
      const res = await axios.post(
        `${REACT_APP_API_TENANTS_URL}/setTenantLogo`,
        formData,
        {
          headers: {
            withCredentials: true,
            "X-Authorization": AUTH_TOKEN(),
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (res.data && res.data.Data) {
        setTenantLogo(res.data.Data);
        notification.open({
          type: "success",
          content: "Logo uploaded successfully",
        });
      }

      setLogoUploading(false);
      setLogoFile(null);
      setIsChangingLogo(false);
      return true;
    } catch (error) {
      console.error("handleLogoUpload failed:", error?.response?.status, error?.message);
      notification.open({
        type: "error",
        content: "Failed to upload logo",
      });
      setLogoUploading(false);
      return false;
    }
  };
  return (
    <Row gutter={16}>
      <Col span={24}>
        <div style={{ marginBottom: "16px" }}>
          <div className="upload-container border border-dashed border-[#d9d9d9] rounded-[2px] bg-[#fafafa] p-4 w-full">
            {tenantLogo && !isChangingLogo ? (
              // Display the existing logo
              <div className="flex flex-col items-center">
                <div className="mb-4 max-w-[200px] max-h-[200px] overflow-hidden">
                  <img
                    src={tenantLogo}
                    alt="Tenant Logo"
                    className="w-full object-contain"
                  />
                </div>
                <div>
                  <Button
                    type="primary"
                    onClick={() => setIsChangingLogo(true)}
                    style={{ minWidth: "120px" }}
                  >
                    Change Logo
                  </Button>
                </div>
              </div>
            ) : logoFile ? (
              // Display the selected file preview
              <div className="flex flex-col items-center">
                <div className="mb-4 max-w-[200px] max-h-[200px] overflow-hidden">
                  <img
                    src={logoFile.url}
                    alt="Logo preview"
                    className="w-full object-contain"
                  />
                </div>
                <div className="flex w-full justify-center gap-2">
                  <Button
                    onClick={() => {
                      setLogoFile(null);
                      if (tenantLogo) {
                        setIsChangingLogo(false);
                      }
                    }}
                    style={{ minWidth: "80px" }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    onClick={() => handleLogoUpload(logoFile.originFileObj)}
                    loading={logoUploading}
                    className="min-w-[80px]"
                  >
                    Upload
                  </Button>
                </div>
              </div>
            ) : (
              // Display the upload interface
              <Upload.Dragger
                accept="image/jpeg,image/png"
                showUploadList={false}
                beforeUpload={async (file) => {
                  const isJpgOrPng =
                    file.type === "image/jpeg" || file.type === "image/png";
                  if (!isJpgOrPng) {
                    notification.open({
                      type: "error",
                      content: "You can only upload JPG or PNG files!",
                    });
                    return false;
                  }

                  const isLt2M = file.size / 1024 / 1024 < 2;
                  if (!isLt2M) {
                    notification.open({
                      type: "error",
                      content: "Image must be smaller than 2MB!",
                    });
                    return false;
                  }

                  if (!validateFileName(file)) {
                    return false;
                  }

                  const isDimensionsValid = await validateImageDimensions(file);
                  if (!isDimensionsValid) {
                    return false;
                  }

                  file.preview = URL.createObjectURL(file);
                  setLogoFile({
                    ...file,
                    uid: file.uid || "-1",
                    name: file.name,
                    status: "ready",
                    url: file.preview,
                    originFileObj: file,
                  });
                  return false;
                }}
                maxCount={1}
                className="p-6 bg-transparent w-full"
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined
                    style={{ color: "#40a9ff", fontSize: "48px" }}
                  />
                </p>
                <p className="ant-upload-text" style={{ fontSize: "16px" }}>
                  Click or drag logo to this area
                </p>
                <p className="ant-upload-hint" style={{ color: "#00000073" }}>
                  Support for a single JPG/PNG image under 2MB with 16:9 aspect
                  ratio (1920x1080)
                </p>
                <p className="ant-upload-hint" style={{ color: "#00000073" }}>
                  File name should only contain letters, numbers, periods,
                  underscores, and hyphens
                </p>
              </Upload.Dragger>
            )}
          </div>
        </div>
      </Col>
    </Row>
  );
}
